import { Outlet, createFileRoute, getRouteApi } from '@tanstack/react-router';
import { useEffect } from 'react';
import z from 'zod';

import { RequireGeneratedContext } from '@/components/Auth/RequireGeneratedContext';
import { DEFAULT_PAGE_SIZE } from '@/components/BaseTable/BaseTable';
import { AccessTokensPageContent } from '@/components/PersonalAccessTokens/AccessTokensPageContent';
import { SettingsPageLayout } from '@/components/Settings/SettingsPageLayout';
import { useUser } from '@/hooks/useUser';
import { searchParam } from '@/utils/router';

const accessTokensSearchSchema = z.object({
  tab: z.enum(['personal', 'machines']).default('personal').catch('personal'),
  pPageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  pPageIndex: z.number().default(0).catch(0),
  pSearch: searchParam().optional(),
  pStatus: z.enum(['active', 'expired']).optional().catch(undefined),
  pNew: z.boolean().default(false).catch(false),
  mPageSize: z.number().default(DEFAULT_PAGE_SIZE).catch(DEFAULT_PAGE_SIZE),
  mPageIndex: z.number().default(0).catch(0),
  mSearch: searchParam().optional(),
  mNew: z.boolean().default(false).catch(false),
});

export const Route = createFileRoute('/$organizationName/settings/access-tokens')({
  component: GatedAccessTokensPage,
  validateSearch: accessTokensSearchSchema,
});

const accessTokensRouteApi = getRouteApi('/$organizationName/settings/access-tokens');

// Tokens authenticate MCP and API calls, which serve generated context; the
// Machines tab lives here too.
function GatedAccessTokensPage() {
  return (
    <RequireGeneratedContext>
      <AccessTokensPage />
    </RequireGeneratedContext>
  );
}

function AccessTokensPage() {
  const { organizationDisplayName } = useUser();
  const search = accessTokensRouteApi.useSearch();
  const navigate = accessTokensRouteApi.useNavigate();
  const { tab } = search;

  const effectiveTab = tab;

  useEffect(() => {
    document.title = `${organizationDisplayName || 'Driver'} - Access Tokens`;
  }, [organizationDisplayName]);

  const handleTabChange = (newTab: string) => {
    const validTab = newTab as 'personal' | 'machines';
    navigate({
      search: (prev) => ({ ...prev, tab: validTab }),
    });
  };

  return (
    <SettingsPageLayout
      fullWidthWithPadding
      newHeader
      hideSeparator
      title="Access Tokens"
      subTitle="Manage personal access tokens and machine tokens for programmatic access to Driver."
    >
      <AccessTokensPageContent effectiveTab={effectiveTab} onTabChange={handleTabChange} />
      <Outlet />
    </SettingsPageLayout>
  );
}
